import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import api from "../services/api";
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, InputAdornment, Autocomplete, MenuItem,
  Chip, CircularProgress, Button, Alert, Stack, Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import EmailPriceListDialog from "./EmailPriceListDialog";

/* =====================================================================
 * The Glomets price list.
 *
 * The price dashboard collects the same material from every yard on the
 * same day - ACSR 67% comes in at 0.93 from Derichebourg, 1.03 from EMR,
 * 1.10 from SIMS and 1.13 from Texas Port. Which of those to work from is
 * a decision that changes by material: EMR for AL CAST, C&Y for Sealed
 * Units.
 *
 * So each of our products carries two choices - which market material it
 * is, and whose price to take - and the list shows what that comes to.
 * With no vendor picked it falls back to the highest quote of the day,
 * which is the number to look at when deciding what we can afford to pay.
 *
 * The margin column takes a percentage off, so the list follows the
 * market instead of needing to be retyped every morning.
 * ===================================================================== */

const money = (n) =>
  n == null ? "—" : "$" + Number(n).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/* The company name for the printed sheet. A price list handed to a supplier
 * with no name on it is just a piece of paper. */
const companyName = (() => {
  try { return JSON.parse(localStorage.getItem("user") || "{}").companyName || "GLOMETS INC"; }
  catch { return "GLOMETS INC"; }
})();

export default function GlometsPriceList() {
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [priceDate, setPriceDate] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [onlyLinked, setOnlyLinked] = useState(false);

  /* What is currently typed in the two number boxes, before it is saved.
   *
   * These have to be controlled inputs so they refresh when the server
   * answers - but a controlled input with no onChange is read-only in React,
   * which is exactly what went wrong: the boxes looked editable and would
   * not accept a keystroke. Typing updates the draft, leaving the box saves
   * it, and the draft is dropped so the server's value shows again. */
  const [company, setCompany] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailOk, setMailOk] = useState(null);   // null until asked
  const [drafts, setDrafts] = useState({});
  const draftKey = (itemId, field) => `${itemId}:${field}`;
  const draftOf = (row, field, serverValue) => {
    const k = draftKey(row.itemId, field);
    return k in drafts ? drafts[k] : (serverValue ?? "");
  };
  const setDraft = (row, field, value) =>
    setDrafts((d) => ({ ...d, [draftKey(row.itemId, field)]: value }));
  const clearDraft = (row, field) =>
    setDrafts((d) => {
      const next = { ...d };
      delete next[draftKey(row.itemId, field)];
      return next;
    });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, mats, vends, co] = await Promise.all([
        api.get("/priceMappings/priceList"),
        api.get("/priceMappings/canonicals"),
        api.get("/prices/vendors"),
        // The letterhead comes from the company record, so ZD GLOBAL's sheet
        // never carries GLOMETS' address.
        api.get("/config/company").catch(() => ({ data: null })),
      ]);
      setCompany(co.data || null);
      setRows(list.data.rows || []);
      setPriceDate(list.data.priceDate || null);
      const m = mats.data;
      setMaterials(Array.isArray(m) ? m : (m.canonicals || m.rows || []));
      const v = vends.data;
      setVendors(Array.isArray(v) ? v : (v.vendors || v.rows || []));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /* Re-read ONLY the prices, with no spinner.
   *
   * Saving used to call load(), which refetched the price list, the material
   * list, the vendor list and the company details, and flipped `loading` on -
   * so the table was replaced by a spinner and rebuilt on every keystroke or
   * dropdown change. The materials, vendors and letterhead cannot change
   * while you are sat on this screen, so there is nothing to refetch: only
   * the prices move, and they can be swapped in underneath without the
   * screen flinching. */
  const refreshRows = useCallback(async () => {
    try {
      const list = await api.get("/priceMappings/priceList");
      setRows(list.data.rows || []);
      setPriceDate(list.data.priceDate || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Ask once whether the server can actually send mail. Better to grey the
   * button out than let someone fill in a form and fail at the end. */
  useEffect(() => {
    api.get("/priceMappings/mailStatus")
      .then((r) => setMailOk(r.data))
      .catch(() => setMailOk({ ok: false, reason: "could not check" }));
  }, []);

  /* The PDF is built by the server, so the downloaded file and the emailed
   * attachment are the same document. */
  const downloadPdf = async () => {
    try {
      const r = await api.get("/priceMappings/priceList.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const co = company && company.Name ? company.Name.replace(/[^A-Za-z0-9]+/g, "-") : "Price";
      a.href = url;
      a.download = co + "-Price-List-" + (priceDate || "today") + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      alert("Could not build the PDF: " + ((err.response && err.response.data && err.response.data.error) || err.message));
    }
  };

  const save = async (row, changes) => {
    setSavingId(row.itemId);
    try {
      const body = {
        Item_ID: row.itemId,
        CanonicalName: "canonicalName" in changes ? changes.canonicalName : row.canonicalName,
        Vendor_ID: "vendorId" in changes ? changes.vendorId : row.vendorId,
        AdjustPct: "adjustPct" in changes ? changes.adjustPct : row.adjustPct,
        FixedPrice: "fixedPrice" in changes ? changes.fixedPrice : row.fixedPrice,
      };
      await api.post("/priceMappings/priceChoice", body);
      // Only this row's draft is finished with. Clearing every draft threw
      // away anything half-typed in another row.
      clearDraft(row, "adjustPct");
      clearDraft(row, "fixedPrice");
      await refreshRows();   // prices only, quietly
    } catch (err) {
      alert("Could not save: " + (err.response?.data?.error || err.message));
    } finally {
      setSavingId(null);
    }
  };

  const materialNames = useMemo(
    () => materials.map((m) => (typeof m === "string" ? m : m.CanonicalName || m.name || "")).filter(Boolean),
    [materials]
  );

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyLinked && !r.canonicalName) return false;
      if (!needle) return true;
      return (r.product || "").toLowerCase().includes(needle) ||
             (r.canonicalName || "").toLowerCase().includes(needle);
    });
  }, [rows, search, onlyLinked]);

  /* Only materials that actually have a price belong on a printed list -
   * an unpriced line tells a supplier nothing. Respects the search and the
   * linked-only filter, so you can print a section rather than everything. */
  const printable = useMemo(
    () => shown.filter((r) => r.finalPrice != null),
    [shown]
  );

  const linked = rows.filter((r) => r.canonicalName).length;
  const priced = rows.filter((r) => r.finalPrice != null).length;
  const ownPriced = rows.filter((r) => r.isFixed).length;

  return (
    <Box p={1}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Glomets Price List</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Follow a yard's price with a margin off it, or type your own price in
              the last column to ignore the market for that material.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {priceDate && <Chip size="small" label={`Prices from ${priceDate}`} color="primary" variant="outlined" />}
            <Chip size="small" label={`${linked} of ${rows.length} linked`} />
            <Chip size="small" color={priced ? "success" : "default"} label={`${priced} priced`} />
            {ownPriced > 0 && (
              <Chip size="small" color="secondary" variant="outlined"
                    label={`${ownPriced} your own price`} />
            )}
            <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
            <Button
              size="small" variant="outlined" startIcon={<PrintIcon />}
              disabled={!priced}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
              disabled={!priced}
              onClick={downloadPdf}
            >
              Download PDF
            </Button>
            <Tooltip title={mailOk && !mailOk.ok
              ? "Email is not set up on the server yet"
              : "Send the price list to a few suppliers"}>
              <span>
                <Button
                  size="small" variant="contained" startIcon={<EmailIcon />}
                  disabled={!priced || !(mailOk && mailOk.ok)}
                  onClick={() => setMailOpen(true)}
                >
                  Email
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
          <TextField
            size="small" placeholder="Search material or product"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          <Button size="small" variant={onlyLinked ? "contained" : "outlined"}
                  onClick={() => setOnlyLinked((v) => !v)}>
            {onlyLinked ? "Showing linked only" : "Show linked only"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Our material</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Market material</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 170 }}>Price from</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Market</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, width: 110 }}>Margin %</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Our price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.itemId} hover
                  sx={{ opacity: savingId === r.itemId ? 0.55 : 1,
                        transition: "opacity .15s ease" }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{r.product}</Typography>
                    {r.group && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{r.group}</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Autocomplete
                      size="small" freeSolo={false} options={materialNames}
                      value={r.canonicalName || null}
                      onChange={(_, v) => save(r, { canonicalName: v })}
                      renderInput={(p) => (
                        <TextField {...p} placeholder="not linked" variant="standard" />
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      select size="small" variant="standard" fullWidth
                      value={r.vendorId ?? ""}
                      disabled={!r.canonicalName}
                      onChange={(e) => save(r, { vendorId: e.target.value === "" ? null : Number(e.target.value) })}
                    >
                      <MenuItem value="">
                        <em>Highest of the day</em>
                      </MenuItem>
                      {vendors.map((v) => (
                        <MenuItem key={v.Vendor_ID} value={v.Vendor_ID}>{v.Name || v.Code}</MenuItem>
                      ))}
                    </TextField>
                    {r.canonicalName && r.usingBest && r.vendorName && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        today: {r.vendorName}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ opacity: r.isFixed ? 0.4 : 1 }}>
                    {r.marketPrice == null && r.canonicalName ? (
                      <Tooltip title="That yard did not quote this material today">
                        <Chip size="small" label="no quote" color="warning" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Box>
                        <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>{money(r.marketPrice)}</Typography>
                        {r.quoteCount > 1 && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {r.quoteCount} yards quoted
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ opacity: r.isFixed ? 0.4 : 1 }}>
                    <TextField
                      size="small" variant="standard" type="number"
                      value={draftOf(r, "adjustPct", r.adjustPct)}
                      disabled={!r.canonicalName || r.isFixed}
                      placeholder="0"
                      onChange={(e) => setDraft(r, "adjustPct", e.target.value)}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? null : Number(e.target.value);
                        clearDraft(r, "adjustPct");
                        if (v !== (r.adjustPct ?? null)) save(r, { adjustPct: v });
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      inputProps={{ step: "0.5", style: { textAlign: "right" } }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>

                  {/* Type a number here and it becomes OUR price - the yard and
                    * the margin stop applying and the material no longer follows
                    * the market. Clear it and the market takes over again. */}
                  <TableCell align="right">
                    <TextField
                      size="small" variant="standard" type="number"
                      value={draftOf(r, "fixedPrice", r.fixedPrice)}
                      placeholder={r.finalPrice == null ? "—" : String(r.finalPrice)}
                      onChange={(e) => setDraft(r, "fixedPrice", e.target.value)}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? null : Number(e.target.value);
                        clearDraft(r, "fixedPrice");
                        if (v !== (r.fixedPrice ?? null)) save(r, { fixedPrice: v });
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      inputProps={{
                        step: "0.01", min: "0",
                        style: {
                          textAlign: "right", fontWeight: 700,
                          color: r.isFixed ? "#7b1fa2" : "#2e7d32",
                        },
                      }}
                      sx={{ width: 110 }}
                    />
                    <Typography variant="caption" sx={{
                      display: "block", color: r.isFixed ? "secondary.main" : "text.secondary",
                    }}>
                      {r.isFixed ? "your price" : r.finalPrice == null ? "" : "from market"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {!shown.length && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
                    Nothing matches that search.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
        Margin is a percentage off the market price: −10 means pay 10% under market.
        Typing in "Our price" sets your own figure and that material stops following
        the market — clear the box to hand it back. Changes save as soon as you leave
        the box, or pressing Enter. Print shows only materials that have a price.
      </Typography>

      {/* ================= THE PRINTED SHEET =================
        * Hidden on screen, and the only thing on the page when printed.
        *
        * Printing the screen itself would put dropdowns, a search box and a
        * Refresh button on paper. What a supplier needs is the material and
        * the price, so the printed version is built separately: no controls,
        * no market column, no margin - just what we pay, with the date it
        * was priced on. */}
      <EmailPriceListDialog
        open={mailOpen}
        onClose={() => setMailOpen(false)}
        company={company}
        priceDate={priceDate}
        count={priced}
      />

      {/* Rendered into <body>, not inside the app.
        *
        * Hiding the app with visibility:hidden was not enough - hidden
        * elements still occupy their space, so the app kept generating blank
        * pages behind the sheet and a one-page list printed as eight. The app
        * has to be display:none, and that is only possible if the sheet is
        * not inside it. */}
      {ReactDOM.createPortal(
      <Box className="price-print-sheet">
        <Box className="pp-head">
          <div className="pp-company">{(company && company.Name) || companyName}</div>
          {company && company.Address && (
            <div className="pp-addr">{company.Address.toUpperCase()}</div>
          )}
          {company && (company.Contact || company.PurchaseContact) && (
            <div className="pp-addr">
              {[company.Contact, company.PurchaseContact]
                .filter(Boolean)
                .map((s) => s.replace(/\s{2,}/g, " ").trim())
                .join("   &   ")}
            </div>
          )}
          <div className="pp-title">Price List</div>
          <div className="pp-date">
            Prices as of {priceDate || "—"} · printed {todayLabel()}
          </div>
        </Box>

        {/* Columns rather than a table. A table of 47 materials runs to three
          * or four sheets; the same list flowing down three narrow columns
          * fits on one, which is what gets pinned up in the yard. The type
          * shrinks a step once the list is long enough to need it. */}
        <div className={"pp-cols" + (printable.length > 60 ? " pp-tight" : "")}>
          {printable.map((r) => (
            <div className="pp-item" key={r.itemId}>
              <span className="pp-name">{r.product}</span>
              <span className="pp-dots" />
              <span className="pp-price">{money(r.finalPrice)}</span>
            </div>
          ))}
        </div>

        <div className="pp-foot">
          Prices are per pound and subject to change without notice.
          Material must be sorted and free of contamination.
        </div>
      </Box>,
      document.body)}

      <style>{`
        .price-print-sheet { display: none; }
        @media print {
          /* The app is REMOVED from the page, not just made invisible.
             visibility:hidden leaves the space behind, which is what turned a
             one-page list into eight pages of blank paper. */
          body > #root { display: none !important; }
          body > .price-print-sheet {
            display: block !important;
            font-family: Georgia, "Times New Roman", serif; color: #000;
            /* Our own margin, because the page has none - see @page below. */
            padding: 12mm 10mm;
            box-sizing: border-box;
          }

          .pp-head { text-align: center; border-bottom: 2px solid #000;
                     padding-bottom: 6px; margin-bottom: 10px; }
          .pp-company { font-size: 20pt; font-weight: 700; letter-spacing: .5px; line-height: 1.1; }
          .pp-addr    { font-size: 9pt; margin-top: 2px; }
          .pp-title   { font-size: 11pt; letter-spacing: 3px; text-transform: uppercase;
                        margin-top: 5px; font-weight: 700; }
          .pp-date    { font-size: 8pt; margin-top: 3px; }

          /* Three columns turn a four-page table into one sheet. */
          .pp-cols { column-count: 3; column-gap: 14px; column-fill: balance; }
          .pp-item {
            display: flex; align-items: baseline; gap: 3px;
            font-size: 8.5pt; line-height: 1.45;
            break-inside: avoid; page-break-inside: avoid;
            border-bottom: 1px dotted #bbb;
          }
          .pp-tight .pp-item { font-size: 7.5pt; line-height: 1.3; }
          .pp-name  { flex: 0 1 auto; overflow: hidden; text-overflow: ellipsis;
                      white-space: nowrap; }
          .pp-dots  { flex: 1 1 auto; }
          .pp-price { font-weight: 700; font-variant-numeric: tabular-nums;
                      white-space: nowrap; }

          .pp-foot { margin-top: 10px; padding-top: 5px; border-top: 1px solid #000;
                     font-size: 8pt; text-align: center; }

          /* Zero page margin is what removes the browser's own header and
           * footer - the date and title along the top, and the
           * app.glomets.com/main line along the bottom. Chrome only prints
           * those into the margin area, so with no margin there is nowhere
           * for them to go. The sheet supplies its own padding instead, so
           * nothing ends up against the edge of the paper. */
          @page { margin: 0; }
        }
      `}</style>
    </Box>
  );
}

/* Today, written the way a person reads it. */
function todayLabel() {
  const d = new Date();
  return d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}
